import { UserOffice } from "@type/core/auth/auth.types";
import { FunctionComponent } from "react";
import { useTranslation } from "react-i18next";
import { SignaturePicturePriorityEnum } from "@utils/enums/signature.picture.priority.enum";
import { requestGetOfficeScellementDefault } from "@state/picture/PictureEffects";
import configuration from "@utils/Config";
import { getToken } from "@utils/rest/RestClient";
import { requestUpdateOfficePicture } from "@state/offices/OfficesEffects";
import SignaturePicture from "@components/core/subComponents/signature/SignaturePicture";
import { toastError, toastSuccess } from "@utils/helpers/toast-helper";
import { requestGetUserInfo } from "@state/auth/AuthEffects";
import useOfficeSignature from "@utils/hooks/useOfficeSignature";
import EnumHelper from "@utils/helpers/enum-helper";
import PictureDefault from "@components/core/preferences/PictureDefault";

interface SignaturePictureDefaultProps {
  office?: UserOffice;
}

const ScellementPictureDefault: FunctionComponent<
  SignaturePictureDefaultProps
> = ({ office }) => {
  const { t } = useTranslation();

  const defaultScellementPriority = EnumHelper.stringToEnum(
    SignaturePicturePriorityEnum,
    office?.scellementPriority?.toUpperCase() ?? "",
  );
  const scellementPictureDefault = office?.scellementPicture;

  const {
    pictures,
    priority,
    setPriority,
    handleDelete,
    handleDefault,
    buildPictureOfficeUrl,
  } = useOfficeSignature(
    "ScellementPicture",
    defaultScellementPriority,
    scellementPictureDefault,
    office?.id,
  );

  const handleUpdatePicture = (
    rank: number,
    file?: File,
    callback?: () => void,
  ) => {
    const formData = new FormData();
    formData.append("scellementPicture", file || "none");
    formData.append("rank", rank.toString());
    void requestUpdateOfficePicture({
      dto: formData,
      id: office?.id,
    })
      .then(() => {
        if (buildPictureOfficeUrl(scellementPictureDefault) === "") {
          void requestGetUserInfo({}).then((user) => {
            if (user.data) {
              user.data.officesForCgu.map((office) => {
                if (office.scellementPicture) {
                  office.scellementPicture.map((picturePath) => {
                    void requestGetOfficeScellementDefault(
                      `${configuration.backendBaseUrl}${picturePath.picture}&token=${getToken()}`,
                    );
                    callback?.();
                  });
                }
              });
            }
          });
        } else {
          void requestGetOfficeScellementDefault(
            buildPictureOfficeUrl(scellementPictureDefault),
          );
          callback?.();
        }
        setPriority(SignaturePicturePriorityEnum.SCELLEMENT_PICTURE);
        toastSuccess(t("offices.preferences.errors.pictureUpdateSuccess"));
      })
      .catch(() => {
        toastError(t("offices.preferences.errors.pictureUpdateError"));
      });
  };

  const handleDeletePicture = () => {
    handleDelete(SignaturePicturePriorityEnum.SCELLEMENT_PICTURE);
  };

  const handleSetDefault = (
    scellementPriority: SignaturePicturePriorityEnum,
    rank?: number,
  ) => {
    handleDefault(scellementPriority, rank);
  };

  return (
    <PictureDefault
      i18nType="scellementPicture"
      i18nModalLoadValue="loadScellementPicture"
      i18nModalAbsenceValue="NoScellementPicture"
      priority={priority}
      handleSetDefault={handleSetDefault}
    >
      <SignaturePicture
        signaturePictures={pictures}
        onUpdatePicture={handleUpdatePicture}
        onDeletePicture={handleDeletePicture}
        onSetDefaultPicture={handleSetDefault}
        signatureType={SignaturePicturePriorityEnum.SCELLEMENT_PICTURE}
        signatureSelected={priority}
        row
      />
    </PictureDefault>
  );
};

export default ScellementPictureDefault;
